// Adım 1 — Yükle (M16 T11; mockup 04, D8 aşama zaman çizelgesi). Dosya → kanıt belgesi
// (presign/PUT/commit, `uploadFileWithProgress`) → tarama beklemesi → job → 2 sn poll. failed →
// errorDetail + "Yeniden dene" / "Eşleştirmesiz devam". ready ama 0 satır → Adım 1'de kal + uyarı.
// Sağda "Taslak içe aktarımlar" (kaldığı yerden devam).
// 2026-10-08 (güvenlik/UX taraması ek iş): backend işi yalnız `scanStatus === "clean"` dosyada açar
// (dev mock tarama ~2 sn) — commit sonrası `waitForCleanScan` (1 sn aralık, ≤90 sn) "Dosya
// taranıyor…" durumuyla bekler; infected/quarantined/error → hata, iş açılmaz; zaman aşımı ve
// createJob `scan_pending` yarışı aynı "Tarama uzun sürdü" metnini verir.

import { useCallback, useEffect, useRef, useState } from "react";
import { KPI_IMPORT_JOB_STATUSES, type KpiImportJobStatus } from "@mahir/shared";
import { INTERNAL_IMPORT_API, type ImportApi, type KpiImportJobDto } from "../../../api/kpi-import";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import {
  isScanBlockedError,
  isScanPendingError,
  waitForCleanScan,
} from "../../../lib/kpi-import-scan";
import {
  KPI_IMPORT_LARGE_BYTES,
  KPI_IMPORT_MAX_BYTES,
  formatBytes,
  gridRowCount,
  kpiImportFileKind,
  kpiImportRejectReason,
  stepForJobStatus,
} from "../../../lib/kpi-import-wizard";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { FileDropzone } from "../../common/FileDropzone";
import { amberBox, btnGhost, btnSmall, errorBox, pillStyle } from "../kpi-form-styles";

const POLL_MS = 2000;
const ACCEPT =
  ".xlsx,.pdf,.docx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const RUNNING: readonly KpiImportJobStatus[] = ["queued", "extracting", "mapping"];

type UploadPhase =
  | { kind: "idle" }
  | { kind: "uploading"; name: string; size: number; pct: number }
  | { kind: "scanning"; name: string; size: number }
  | { kind: "creating"; name: string; size: number }
  | { kind: "error"; name: string; size: number; message: string };

const BUSY_PHASES: readonly UploadPhase["kind"][] = ["uploading", "scanning", "creating"];

export interface ImportUploadStepProps {
  clientId: string;
  setId: string;
  job: KpiImportJobDto | null;
  onJob: (job: KpiImportJobDto | null) => void;
  onReady: (job: KpiImportJobDto) => void;
  onResume: (jobId: string) => void;
  onCancel: () => void;
  /** T19 — API tabanı (iç / portal) */
  api?: ImportApi;
  /** T19 — müşteri modu: aşama kopyasında AI izi yok */
  isClient?: boolean;
}

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--color-border)",
  borderRadius: 12,
  padding: 16,
  background: "var(--color-surface)",
};

function stageIndex(status: KpiImportJobStatus): number {
  // 0 okundu · 1 sayfa bulundu · 2 eşleştiriyor · 3 hazır
  if (status === "queued") return -1;
  if (status === "extracting") return 0;
  if (status === "mapping") return 2;
  if (status === "ready" || status === "committed") return 4;
  return -2; // failed/draft
}

function StageLine({
  state,
  text,
  hint,
}: {
  state: "done" | "active" | "todo";
  text: string;
  hint?: string;
}): JSX.Element {
  const mark = state === "done" ? "✓" : state === "active" ? "●" : "○";
  const color =
    state === "done"
      ? "var(--color-primary)"
      : state === "active"
        ? "var(--color-primary)"
        : "var(--color-text-3)";
  return (
    <li
      className="flex items-start gap-2 text-sm"
      style={{ color: state === "todo" ? "var(--color-text-3)" : "var(--color-text)" }}
    >
      <span aria-hidden="true" style={{ color, width: 16, display: "inline-block" }}>
        {mark}
      </span>
      <span>
        {text}
        {hint ? <span style={{ color: "var(--color-text-3)", marginLeft: 6 }}>{hint}</span> : null}
      </span>
    </li>
  );
}

function ProcessingCard({
  job,
  onRetry,
  onSkipMapping,
  busy,
  isClient,
}: {
  job: KpiImportJobDto;
  onRetry: () => void;
  onSkipMapping: () => void;
  busy: boolean;
  isClient: boolean;
}): JSX.Element {
  const idx = stageIndex(job.status);
  const failed = job.status === "failed";
  const rows = gridRowCount(job.grid);
  const sheets = job.grid?.sheets.length ?? 0;
  const st = (n: number): "done" | "active" | "todo" =>
    idx > n ? "done" : idx === n ? "active" : "todo";
  const u = KPI_IMPORT_COPY.upload;
  return (
    <div style={cardStyle} aria-live="polite">
      <div className="font-semibold mb-2" style={{ fontSize: "0.95rem" }}>
        {failed ? u.stageFailed : u.processingTitle}
      </div>
      {!failed ? (
        <ol className="flex flex-col gap-1" style={{ listStyle: "none", padding: 0 }}>
          <StageLine
            state={idx === -1 ? "active" : "done"}
            text={idx === -1 ? u.stageQueued : u.stageExtracting}
          />
          <StageLine
            state={st(1)}
            text={sheets > 0 ? u.sheetsFound(sheets, rows) : "Sayfalar bulunuyor"}
          />
          <StageLine
            state={st(2)}
            text={isClient ? KPI_IMPORT_COPY.client.stageMapping : u.stageMapping}
            {...(idx === 2 ? { hint: u.stageMappingHint } : {})}
          />
          <StageLine state={st(3)} text={u.stageReady} />
        </ol>
      ) : (
        <div role="alert" style={errorBox}>
          {job.errorDetail ??
            (isClient ? KPI_IMPORT_COPY.client.failedDetail : KPI_IMPORT_COPY.genericError)}
        </div>
      )}
      {job.stage ? (
        <div className="mt-2" style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
          {job.stage}
          {job.attempts > 0 ? ` · deneme ${(job.attempts + 1).toString()}` : ""}
        </div>
      ) : null}
      <div className="flex items-center gap-3 mt-3 flex-wrap text-sm">
        <span style={{ color: "var(--color-text-3)" }}>{u.troubleLabel}</span>
        <button type="button" style={btnSmall} disabled={busy} onClick={onRetry}>
          {u.retry}
        </button>
        <button type="button" style={btnSmall} disabled={busy || !job.grid} onClick={onSkipMapping}>
          {u.skipMapping}
        </button>
      </div>
    </div>
  );
}

function DraftList({
  setId,
  currentJobId,
  onResume,
  refreshKey,
  api,
}: {
  setId: string;
  currentJobId: string | null;
  onResume: (id: string) => void;
  refreshKey: number;
  api: ImportApi;
}): JSX.Element {
  const [jobs, setJobs] = useState<KpiImportJobDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<KpiImportJobDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [localKey, setLocalKey] = useState(0);
  useEffect(() => {
    let alive = true;
    api
      .listJobs(setId)
      .then((j) => {
        if (alive) setJobs(j.filter((x) => KPI_IMPORT_JOB_STATUSES.includes(x.status)));
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : KPI_IMPORT_COPY.genericError);
      });
    return () => {
      alive = false;
    };
  }, [setId, refreshKey, localKey, api]);
  const u = KPI_IMPORT_COPY.upload;
  const others = jobs.filter((j) => j.id !== currentJobId);

  async function confirmDelete(): Promise<void> {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.deleteJob(toDelete.id);
      setToDelete(null);
      setLocalKey((k) => k + 1);
    } catch (err) {
      setError(`${u.deleteFailed}: ${err instanceof Error ? err.message : ""}`.trim());
      setToDelete(null);
    } finally {
      setDeleting(false);
    }
  }
  return (
    <aside
      style={{ ...cardStyle, minWidth: 280, maxWidth: 340 }}
      aria-labelledby="kpi-import-drafts"
    >
      <div id="kpi-import-drafts" className="font-semibold mb-2" style={{ fontSize: "0.95rem" }}>
        {u.draftsTitle}
      </div>
      {error ? (
        <div className="text-sm" style={{ color: "var(--color-error)" }}>
          {error}
        </div>
      ) : others.length === 0 ? (
        <div className="text-sm" style={{ color: "var(--color-text-3)" }}>
          {u.draftsEmpty}
        </div>
      ) : (
        <ul className="flex flex-col gap-3" style={{ listStyle: "none", padding: 0 }}>
          {others.map((j) => (
            <li
              key={j.id}
              style={{ borderBottom: "1px solid var(--color-border)", paddingBottom: 8 }}
            >
              <div className="font-semibold text-sm truncate" title={j.fileName}>
                {j.fileName}
              </div>
              <div style={{ fontSize: "0.78rem", color: "var(--color-text-3)" }}>
                {u.draftStep(stepForJobStatus(j.status))} ·{" "}
                {new Date(j.updatedAt).toLocaleString("tr-TR")}
                {j.status === "failed"
                  ? ` · ${u.stageFailed.toLocaleLowerCase("tr")}`
                  : j.stage
                    ? ` · ${j.stage}`
                    : ""}
              </div>
              <div className="flex items-center gap-3 mt-1">
                <button
                  type="button"
                  className="text-sm"
                  style={{
                    color: "var(--color-primary)",
                    fontWeight: 600,
                    background: "none",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => onResume(j.id)}
                >
                  {u.resume}
                </button>
                <button
                  type="button"
                  className="text-sm"
                  aria-label={`${j.fileName} taslağını sil`}
                  style={{
                    color: "var(--color-error)",
                    background: "none",
                    border: 0,
                    padding: 0,
                    cursor: "pointer",
                  }}
                  onClick={() => setToDelete(j)}
                >
                  {u.deleteDraft}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3" style={{ fontSize: "0.75rem", color: "var(--color-text-3)" }}>
        {u.draftsNote}
      </div>
      <ConfirmDialog
        open={toDelete !== null}
        title={u.deleteConfirmTitle}
        body={u.deleteConfirmBody(toDelete?.fileName ?? "")}
        confirmLabel={u.deleteConfirm}
        tone="danger"
        busy={deleting}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setToDelete(null)}
      />
    </aside>
  );
}

export function ImportUploadStep(props: ImportUploadStepProps): JSX.Element {
  const { clientId, setId, job, onJob, onReady, onResume, onCancel } = props;
  const api = props.api ?? INTERNAL_IMPORT_API;
  const isClient = props.isClient ?? false;
  const [phase, setPhase] = useState<UploadPhase>({ kind: "idle" });
  const [busy, setBusy] = useState(false);
  const [noRows, setNoRows] = useState(false);
  const [draftsKey, setDraftsKey] = useState(0);
  const fileRef = useRef<File | null>(null);
  const u = KPI_IMPORT_COPY.upload;

  // 2 sn poll — iş koşarken
  useEffect(() => {
    if (!job || !RUNNING.includes(job.status)) return;
    const t = setInterval(() => {
      api
        .getJob(job.id)
        .then((fresh) => {
          onJob(fresh);
          if (fresh.status === "ready") {
            if (gridRowCount(fresh.grid) === 0) setNoRows(true);
            else onReady(fresh);
          }
        })
        .catch(() => {
          // geçici ağ hatası — bir sonraki tur
        });
    }, POLL_MS);
    return () => clearInterval(t);
  }, [job, onJob, onReady, api]);

  // Taslaktan gelen `ready` job (0 satır) — uyarı
  useEffect(() => {
    if (job?.status === "ready" && gridRowCount(job.grid) === 0) setNoRows(true);
  }, [job]);

  const start = useCallback(
    async (file: File): Promise<void> => {
      const kind = kpiImportFileKind(file);
      if (kind === null || kind === "xls") return;
      fileRef.current = file;
      setNoRows(false);
      setPhase({ kind: "uploading", name: file.name, size: file.size, pct: 0 });
      try {
        const evidence = await api.uploadFile({ clientId, setId }, file, kind, (pct) =>
          setPhase({ kind: "uploading", name: file.name, size: file.size, pct }),
        );
        // tarama beklemesi — iş yalnız `clean` dosyada açılır (2026-10-08)
        setPhase({ kind: "scanning", name: file.name, size: file.size });
        const scan = await waitForCleanScan(
          async () => (await api.getEvidence(evidence.id)).scanStatus,
        );
        if (scan !== "clean") {
          setPhase({
            kind: "error",
            name: file.name,
            size: file.size,
            message:
              scan === "blocked" ? u.scanBlocked : scan === "error" ? u.scanError : u.scanTimeout,
          });
          return;
        }
        setPhase({ kind: "creating", name: file.name, size: file.size });
        const created = await api.createJob(setId, evidence.id);
        onJob(created);
        setDraftsKey((k) => k + 1);
        setPhase({ kind: "idle" });
      } catch (err) {
        setPhase({
          kind: "error",
          name: file.name,
          size: file.size,
          message: isScanPendingError(err)
            ? u.scanTimeout
            : isScanBlockedError(err)
              ? u.scanBlocked
              : err instanceof Error
                ? err.message
                : KPI_IMPORT_COPY.genericError,
        });
      }
    },
    [clientId, setId, onJob, api, u],
  );

  async function retry(skipMapping: boolean): Promise<void> {
    if (!job) return;
    setBusy(true);
    setNoRows(false);
    try {
      const next = await api.retry(job.id, skipMapping ? { skipMapping: true } : {});
      onJob(next);
      if (next.status === "ready" && next.grid && gridRowCount(next.grid) > 0) onReady(next);
    } catch (err) {
      setPhase({
        kind: "error",
        name: job.fileName,
        size: 0,
        message: err instanceof Error ? err.message : KPI_IMPORT_COPY.genericError,
      });
    } finally {
      setBusy(false);
    }
  }

  const fileInfo =
    phase.kind !== "idle"
      ? { name: phase.name, size: phase.size }
      : job
        ? { name: job.fileName, size: 0 }
        : null;
  const showDropzone = !job || job.status === "failed" || noRows;

  return (
    <div className="flex gap-4 items-start flex-wrap">
      <div className="flex flex-col gap-3" style={{ flex: "1 1 560px", minWidth: 0 }}>
        {showDropzone && !BUSY_PHASES.includes(phase.kind) ? (
          <>
            <FileDropzone
              accept={ACCEPT}
              multiple={false}
              maxFiles={1}
              maxBytes={KPI_IMPORT_MAX_BYTES}
              label={u.dropLabel}
              hint={u.dropHint}
              rejectReason={kpiImportRejectReason}
              onFiles={(files) => {
                const f = files[0];
                if (f) void start(f);
              }}
            />
            <div className="text-sm" style={{ color: "var(--color-text-2)" }}>
              {u.pdfNote}
            </div>
          </>
        ) : null}

        {noRows ? (
          <div role="alert" style={amberBox}>
            {u.noRows}
          </div>
        ) : null}

        {fileInfo ? (
          <div style={cardStyle}>
            <div className="flex items-center gap-3 flex-wrap">
              <span style={{ ...pillStyle, fontSize: "0.75rem", padding: "4px 10px" }}>
                {(kpiImportFileKind({ name: fileInfo.name }) ?? "dosya").toUpperCase()}
              </span>
              <span
                className="font-semibold text-sm truncate"
                style={{ flex: 1, minWidth: 0 }}
                title={fileInfo.name}
              >
                {fileInfo.name}
                {fileInfo.size > 0 ? (
                  <span style={{ color: "var(--color-text-3)", fontWeight: 400 }}>
                    {" "}
                    · {formatBytes(fileInfo.size)}
                  </span>
                ) : null}
              </span>
              {phase.kind === "uploading" ? (
                <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
                  {u.uploading} %{phase.pct.toString()}
                </span>
              ) : phase.kind === "scanning" ? (
                <span
                  role="status"
                  aria-live="polite"
                  className="text-sm"
                  style={{ color: "var(--color-text-2)" }}
                  data-testid="kpi-import-scanning"
                >
                  {u.scanning}
                </span>
              ) : phase.kind === "creating" ? (
                <span className="text-sm" style={{ color: "var(--color-text-2)" }}>
                  {u.creatingJob}
                </span>
              ) : job ? (
                <span style={pillStyle}>{u.uploaded}</span>
              ) : null}
            </div>
            {phase.kind === "uploading" ? (
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={phase.pct}
                aria-label={u.uploading}
                className="mt-2"
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: "var(--color-bg)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${phase.pct.toString()}%`,
                    height: "100%",
                    background: "var(--color-primary)",
                  }}
                />
              </div>
            ) : null}
            {fileInfo.size > KPI_IMPORT_LARGE_BYTES ? (
              <div className="mt-2 text-sm" style={{ color: "var(--color-warning-text)" }}>
                {u.sizeWarn}
              </div>
            ) : null}
            {phase.kind === "error" ? (
              <div role="alert" className="mt-2" style={errorBox}>
                {phase.message}
                {fileRef.current ? (
                  <button
                    type="button"
                    style={{ ...btnSmall, marginLeft: 10 }}
                    onClick={() => {
                      const f = fileRef.current;
                      if (f) void start(f);
                    }}
                  >
                    {u.retry}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {job && (RUNNING.includes(job.status) || job.status === "failed") ? (
          <ProcessingCard
            job={job}
            busy={busy}
            isClient={isClient}
            onRetry={() => void retry(false)}
            onSkipMapping={() => void retry(true)}
          />
        ) : null}

        {job ? (
          <div>
            <button type="button" style={btnGhost} onClick={onCancel} disabled={busy}>
              {u.cancelLabel}
            </button>
          </div>
        ) : null}
      </div>
      <DraftList
        setId={setId}
        currentJobId={job?.id ?? null}
        onResume={onResume}
        refreshKey={draftsKey}
        api={api}
      />
    </div>
  );
}
